/**
 * Para onde uma PESSOA leva — a AÇÃO PRIMÁRIA DO PAINEL, em forma de rota.
 *
 * O card (`PersonHitCard`) e o Enter da paleta precisavam da mesma resposta e
 * davam respostas diferentes: o botão âmbar do card abria a conversa ativa, e o
 * Enter ia para o cadastro. A paleta se contradizia — dizia com cor que o
 * principal era a conversa e fazia a coisa secundária no gesto mais barato.
 *
 * A REGRA da paleta, decidida pelo TL em 14/Ago: **Enter faz o que o painel
 * direito mostra como ação primária.** Não é a mesma tecla fazendo duas coisas
 * por regra invisível — é a mesma tecla fazendo o que está na tela. O operador
 * VÊ o botão âmbar antes de apertar.
 *
 * Então este hook não decide nada por conta própria: ele espelha o card.
 *   - card mostra «Abrir conversa ativa» → a conversa ativa
 *   - card mostra só «Histórico completo» → `/pessoas/:id#conversas`, a MESMA
 *     rota daquele botão (inclusive a âncora — espelhar pela metade não espelha)
 *
 * O que foi REJEITADO pelo TL, e com razão: janela de tempo («se a conversa é
 * recente vai para ela, se é antiga vai para a ficha»). Seria heurística
 * arbitrada, sem medição, e invisível para quem opera.
 *
 * Por que aqui e não no engine: `PersonsService.list` recebe só `tenantId` —
 * **não conhece o caller**, então não pode autorizar conversa. Devolver um
 * `activeConversationId` de lá vazaria a existência de conversa que o caller não
 * pode abrir, e levaria a paleta a navegar para um 403. `usePersonConversations`
 * já vem com o escopo do caller aplicado pelo servidor.
 *
 * Custo: zero request novo. A `queryKey` é a MESMA que o card usa
 * (`['person-conversations', personId]`), então o React Query dedupa.
 */
import { TERMINAL_CONVERSATION_STATUSES } from './persons-adapter';
import { usePersonConversations } from './use-persons';

export interface PersonPrimaryTarget {
  /** Rota do Enter/clique. `undefined` enquanto não se sabe. */
  to: string | undefined;
  /** true enquanto a resposta não chegou — quem chama espera, não adivinha. */
  loading: boolean;
  /** Quantas conversas ativas a pessoa tem (0 quando não há). */
  activeCount: number;
}

export function usePersonPrimaryTarget(
  personId: string | undefined,
  enabled = true,
): PersonPrimaryTarget {
  const { data, isLoading } = usePersonConversations(personId, enabled);

  if (personId === undefined || personId === '') {
    return { to: undefined, loading: false, activeCount: 0 };
  }

  // Sem resposta ainda: NÃO devolve o fallback. Devolver a ficha aqui faria o
  // Enter apertado rápido cair num lugar e o Enter apertado devagar cair em
  // outro — comportamento inconsistente é pior que comportamento previsível.
  if (data === undefined) {
    return { to: undefined, loading: isLoading, activeCount: 0 };
  }

  const episodes = data.data;
  const active = episodes.filter((e) => !TERMINAL_CONVERSATION_STATUSES.has(e.status));
  const first = active[0];

  return {
    to:
      first !== undefined
        ? `/atendimento/conversation/${first.id}`
        : `/pessoas/${personId}#conversas`,
    loading: false,
    activeCount: active.length,
  };
}
